/* eslint-disable max-len */
import { AllowedFileType, CLIENT_FLOWS } from '@global/constants/general';
import { showSnackbar } from '@services/snackbar';
import { parse } from 'date-fns';
import React, { Dispatch, SetStateAction } from 'react';

import { ProfileVerificationData } from '@containers/form-verification/verification/types';
import { STATE_SORT_PRIORITY } from '@global/constants/verification';
import { VerificationConfig } from '@global/types/form';
import { store } from '@redux/store';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { useLocation } from 'react-router-dom';

export const isClientCareerMosaic = (): boolean => {
  return import.meta.env.VITE_CLIENT_FLOW === CLIENT_FLOWS.CM;
};

// Helper to get the appropriate storage based on client flow
const getStorage = () => {
  return isClientCareerMosaic() ? sessionStorage : localStorage;
};
const storageType = isClientCareerMosaic() ? 'sessionStorage' : 'localStorage';

// Safe storage utilities to handle SecurityError
const isLocalStorageAvailable = (): boolean => {
  try {
    const storage = getStorage();
    const test = '__storage_test__';
    storage.setItem(test, test);
    storage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

const safeLocalStorageGetItem = (key: string): string | null => {
  try {
    const storage = getStorage();
    return storage.getItem(key);
  } catch (e) {
    console.warn(`Failed to access ${storageType} for key "${key}":`, e);
    return null;
  }
};

const safeLocalStorageSetItem = (key: string, value: string): boolean => {
  try {
    const storage = getStorage();
    storage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`Failed to set ${storageType} for key "${key}":`, e);
    return false;
  }
};

const safeLocalStorageRemoveItem = (key: string): boolean => {
  try {
    const storage = getStorage();
    storage.removeItem(key);
    return true;
  } catch (e) {
    console.warn(`Failed to remove ${storageType} for key "${key}":`, e);
    return false;
  }
};

const safeLocalStorageClear = (): boolean => {
  try {
    const storage = getStorage();
    storage.clear();
    return true;
  } catch (e) {
    console.warn(`Failed to clear ${storageType}:`, e);
    return false;
  }
};

// Export safe storage utilities (localStorage/sessionStorage based on client flow) for use throughout the application
export {
  isLocalStorageAvailable,
  safeLocalStorageClear,
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem
};

dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(updateLocale);
dayjs.extend(advancedFormat);
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few secs',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d d',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years'
  }
});

dayjs.extend(customParseFormat);

// eslint-disable-next-line prettier/prettier
const fileSizeLimit = +import.meta.env.VITE_FILE_SIZE_MAX_LIMIT!;
const fileSizeLimitMB = +import.meta.env.VITE_FILE_SIZE_MAX_LIMIT! / 1000000;

const fileSizeMinLimit = +import.meta.env.VITE_FILE_SIZE_MIN_LIMIT!;
const fileSizeMinLimitKB = +import.meta.env.VITE_FILE_SIZE_MIN_LIMIT! / 1000;

export const isLiteralObject = (a: any) => {
  return !!a && a.constructor === Object;
};

export const deleteFromErrors = (
  errors: Record<string, string> | { [key: string]: any }[],
  setErrors:
    | Dispatch<SetStateAction<Record<string, string>>>
    | Dispatch<SetStateAction<{ [key: string]: any }[]>>,
  keys: string[],
  index?: number
) => {
  let tempErrors: any;
  if (Array.isArray(errors) && index !== undefined) {
    tempErrors = [...errors];
    if (keys) {
      keys.forEach((key) => {
        if (tempErrors[index][key] !== undefined) {
          delete tempErrors[index][key];
        }
      });
      if (Object.keys(tempErrors[index])?.length === 0) {
        delete tempErrors[index];
      }
    }
  } else if (isLiteralObject(errors)) {
    tempErrors = { ...errors };
    keys.forEach((key) => {
      if (tempErrors[key] !== undefined) {
        delete tempErrors[key];
      }
    });
  }
  setErrors(tempErrors);
};

export const validateEmail = (value: string) => {
  const input = document.createElement('input');

  input.type = 'email';
  input.required = true;
  input.value = value;

  return typeof input.checkValidity === 'function'
    ? input.checkValidity()
    : /\S+@\S+\.\S+/.test(value);
};

export const getAccessToken = () => {
  return safeLocalStorageGetItem('accessToken');
};

export const convertToTitleCase = (text: string) => {
  if (text === 'dob') {
    return 'Date of Birth';
  }
  // returns false if text includes whitespace, _, or -.
  const isCamelCase = !text.match(/[\s_-]/g);
  let titleCase = '';
  if (isCamelCase) {
    const result = text.replace(/([A-Z])/g, ' $1');
    titleCase = result.charAt(0).toUpperCase() + result.slice(1);
  } else {
    titleCase = text
      .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
      .replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`);
  }
  return titleCase;
};

export function isDesktop() {
  if (window.innerWidth > 1080 && window.innerHeight > 500) {
    return true;
  } else {
    return false;
  }
}

export const getLocation: () => Promise<{ [key: string]: number }> = () =>
  new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          const lat = position?.coords?.latitude;
          const long = position?.coords?.longitude;

          resolve({ latitude: lat, longitude: long });
        },
        () => reject('Location is not enabled.'),
        {
          enableHighAccuracy: false,
          timeout: 8000
        }
      );
    } else {
      reject('Geolocation is not supported.');
    }
  });

export const validateFiles = (
  files: any[],
  allowedFileTypes: AllowedFileType[],
  existingFiles?: any[],
  maxFileCount?: number
) => {
  if (
    maxFileCount !== undefined &&
    existingFiles !== undefined &&
    existingFiles?.length + files.length > maxFileCount
  ) {
    showSnackbar(`Only ${maxFileCount} file(s) can be uploaded`, 'error');
    return [];
  }

  const validFiles = [];
  for (const file of files) {
    if (file?.size > fileSizeLimit) {
      showSnackbar(`File size should be less than ${fileSizeLimitMB} MB`, 'error');
    } else if (file?.size < fileSizeMinLimit) {
      showSnackbar(`File size should be more than ${fileSizeMinLimitKB} KB`, 'error');
    } else if (!allowedFileTypes.includes(file.type)) {
      const typesToShow = allowedFileTypes.map((r) => r?.split('/')?.[1])?.join(', ');
      showSnackbar(`Please upload a file of type: ${typesToShow}`, 'error');
    } else {
      validFiles.push(file);
    }
  }
  return validFiles;
};

export const convertBase64ToFile = (base64Str: string): File => {
  // Extract the base64 part from data URL if present
  let base64Data: string;
  let mimeType: string;

  if (base64Str.includes('data:') && base64Str.includes('base64,')) {
    // It's a data URL format: data:image/jpeg;base64,/9j/4AAQSkZJRgABA...
    const [header, data] = base64Str.split(',');
    base64Data = data;
    mimeType = header.split(';')[0]?.split('data:')[1] || 'application/octet-stream';
  } else {
    // It's just base64 data
    base64Data = base64Str;
    mimeType = 'application/octet-stream'; // Default MIME type
  }

  const binaryStr = atob(base64Data);
  const binaryLen = binaryStr.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  const file = new File([bytes], 'geo-tag-image', {
    type: mimeType
  });
  return file;
};

export const convertDataUrlToFile = (dataUrl: string) => {
  // Extract base64 part (everything after 'base64,')
  const base64Str = dataUrl.split(',')[1];

  // Decode base64 to binary string
  const binaryStr = atob(base64Str);

  // Convert binary string to an array of bytes
  const binaryLen = binaryStr.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }

  // Create a file from the byte array
  const file = new File([bytes], 'geo-tag-image', {
    type: dataUrl.split(';')[0]?.split('data:')[1]
  });

  return file;
};

export const camelCaseToString = (text: string) => {
  if (!text) return;

  const result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const checkIfJson = (data: string) => {
  try {
    if (typeof data === 'object') {
      return {
        isObject: typeof data === 'object',
        data: data
      };
    }
    const json = JSON.parse(data);
    return {
      isObject: typeof json === 'object',
      data: json
    };
  } catch (e) {
    console.info(e);
    return {
      isObject: false,
      data: data
    };
  }
};
export function isDateValid(h = new Date()) {
  try {
    const time = dayjs(h).isValid();
    if (time) {
      return true;
    } else {
      return false;
    }
  } catch (e: any) {
    console.info(e);
    return false;
  }
}

export const displayTime = (
  h: string | number | Date = new Date(),
  format = 'DD-MMM-YYYY HH:mm'
) => {
  // Check if h is a date-only string (YYYY-MM-DD format without time)
  if (typeof h === 'string') {
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyRegex.test(h)) {
      // For date-only strings, parse directly with dayjs to avoid timezone conversion
      const d = dayjs(h).format(format);
      return d;
    } else {
      h = getDateToRender(h) as Date;
    }
  }

  const ago = new Date(h); // convert to decimal unix time
  const d = dayjs(ago).format(format);
  return d;
};

// compares if b is bigger than a
export function isADateBiggerThanB(a = new Date(), b = new Date()) {
  a = new Date(a); // convert to decimal unix time
  b = new Date(b); // convert to decimal unix time
  const d = dayjs(b).isBefore(a, 'day');
  return d;
}

export function addTime(h: any, duration = [1, 'd'], format = 'LLLL') {
  const d = dayjs(new Date(h)).add(Number(duration[0]), duration[1] as dayjs.ManipulateType);
  return d.format(format);
}

export const addOneDay = (value: any) => {
  const date = new Date(value);
  return date.setDate(date.getDate() + 1);
};

export const getTimeInfo = (value: any) => {
  const d = new Date(value);
  return new Date(0, 0, 0, d.getHours(), d.getMinutes());
};

export function trimFields(data: any): any {
  if (typeof data === 'string') {
    return data.trim();
  } else if (Array.isArray(data)) {
    return data.map(trimFields);
  } else if (data !== null && isLiteralObject(data)) {
    const trimmedObject: any = {};
    for (const key in data) {
      if (data[key]) {
        trimmedObject[key] = trimFields(data[key]);
      }
    }
    return trimmedObject;
  } else {
    return data;
  }
}

export const safelyParseJSON = (str: string, type: string = 'object') => {
  let result;
  try {
    result = JSON.parse(str);
  } catch (error) {
    console.info(error);
    result = type === 'array' ? [] : {};
  }
  return result;
};

export const getDateToRender = (val: string | Date) => {
  let parsedVal = val ?? null;
  if (typeof val === 'string' && val?.length > 0) {
    const dateOnlyRegex = /^\d{2}-\d{2}-\d{4}$/;
    const dateWithYearRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (dateOnlyRegex.test(val)) {
      parsedVal = parse(val, 'dd-MM-yyyy', new Date());
    } else if (dateWithYearRegex.test(val)) {
      parsedVal = parse(val, 'yyyy-MM-dd', new Date());
    }
  }
  return parsedVal;
};

export const isValidUrl = (url: string) => {
  const urlPattern = new RegExp(
    '((http|https)://)(www.)?[a-zA-Z0-9@:%._\\+~#?&//=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%._\\+~#?&//=]*)'
  ); // validate fragment locator
  return urlPattern.test(url);
};

export const base64ToBinary = async (b64: string) => {
  return fetch(b64).then((res) => res.blob());
};

export function getTimeAgo(h: Date | string | number, displayAgoFormat = false) {
  const ago = new Date(h); // convert to decimal unix time
  return dayjs(ago).fromNow(!displayAgoFormat);
}

export function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const getProfileVerification = (configData: VerificationConfig, subType?: string) => {
  const category = configData?.category?.name?.toLowerCase();
  const { profile } = store.getState();
  if (category && configData?.name) {
    let foundRecord;
    const verificationList = profile?.verificationDetailList?.[category]?.verificationList ?? [];
    foundRecord = verificationList?.find(
      (r: ProfileVerificationData) => r?.type === configData?.name
    );
    if (subType !== undefined) {
      if (subType === '') {
        foundRecord = undefined;
      } else if (configData?.additionalInfo?.acceptMultipleSubmissions) {
        foundRecord = verificationList?.find(
          (r: ProfileVerificationData) => r?.subType === subType && r?.type === configData?.name
        );
      }
    }
    return foundRecord;
  }
  return undefined;
};

export const isAddressBased = (configData: VerificationConfig) => {
  const found = configData?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.find((r) =>
    r?.name?.includes('Address')
  );
  return Boolean(found);
};

export const sortDataItems = (
  data: any[],
  sortKey = 'state',
  priority = STATE_SORT_PRIORITY
): any[] => {
  return data.sort((a, b) => {
    if (sortKey === 'state') {
      if (a.insufficiency && !b.insufficiency) return -1; // `a` comes first
      if (!a.insufficiency && b.insufficiency) return 1; // `b` comes first
    }
    return (priority[a[sortKey]] || Infinity) - (priority[b[sortKey]] || Infinity);
  });
};

export const getDateFormat = (format: string) => {
  const splitArr = format?.toLowerCase()?.split('-');
  if (!format || splitArr?.length < 3) {
    return 'dd-MM-yyyy';
  }
  // capitalise 'mm' only
  let updatedFormat = '';
  splitArr.forEach((key, idx) => {
    const hyphen = idx === splitArr?.length - 1 ? '' : '-';
    if (key === 'mm') {
      updatedFormat += `${key.toUpperCase()}${hyphen}`;
    } else {
      updatedFormat += `${key}${hyphen}`;
    }
  });
  return updatedFormat;
};

export const updateDocumentTitle = (params: string[] = []) => {
  const title = params.reduce((acc, param) => {
    param = param?.trim();
    if (param) {
      acc += ` ${acc?.length > 0 ? `- ${param}` : param}`;
    }
    return acc;
  }, '');

  document.title =
    title?.length > 1
      ? `${title} ${window?._env_?.title ? '- ' + window?._env_?.title : ''}`
      : (window?._env_?.title ?? '');
};

export const emailTemplate = (email: string) => {
  return `mailto:${email}?subject=Ticket%20Raised%20By%20Email&body=Please%20follow%20this%20template%20for%20faster%20resolution%0A%0AName:%20%5BYour%20Full%20Name%5D%0A%0ARegistered%20Mobile:%20%5BYour%20Mobile%20Number%5D%0A%0A${import.meta.env.VITE_ORG}%20Reference%20Number:%20%5BRef%20No%5D%0A%0AIssue%20Type:%0A1.%20Process%20Delay%0A2.%20Missing%20Document%0A3.%20General%20Query%0A4.%20Payment%20Issue%0A5.%20Refund%20Issue%0A6.%20General%20Enquiry%0A(Please%20select%20the%20relevant%20issue%20type%20and%20remove%20others)%0A%0AQuery:%20%5BEnter%20your%20query%20here%5D%0A%0ATicket%20ID%20(if%20any):%20%5BOptional%5D`;
};

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1];
        if (base64) {
          resolve(base64);
        } else {
          reject(new Error('Base64 data not found in file'));
        }
      } else {
        reject(new Error('File could not be converted to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

/**
 * Evaluates a params function string and merges the result with existing query params.
 * Filters out null and undefined values from the result.
 *
 * @param params - A string representation of a function that returns query parameters
 * @param fieldData - The field data to pass to the params function (typically fields[0])
 * @param additionalData - Additional data to pass to the params function (default: {})
 * @param existingQueryParams - Existing query parameters to merge with (default: {})
 * @returns Merged query parameters object with null/undefined values filtered out
 */
export const evaluateParams = (
  params: string,
  fieldData: any,
  additionalData: any = {}
): Record<string, any> => {
  if (!params) {
    return {};
  }

  const paramsFn = eval(params);
  let extraParams = paramsFn(fieldData, additionalData);
  extraParams = Object.fromEntries(
    Object.entries(extraParams).filter(([, value]) => value !== null && value !== undefined)
  );

  return extraParams;
};
